import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { isLoggedIn, isMustChangePassword } from 'utils/auth';
import { usePermission } from 'components/PermissionProvider';
import { routeMap } from 'App';
import { EXPORT_PERMISSION, EXPORT_RECORDS_PATH } from 'utils/orderExport';
import { collectLeafPaths, findFirstLeafPath } from 'utils/menuTree';

export const LoginGuard: React.FC = () => {
    if (!isLoggedIn()) {
        return <Navigate to="/login" replace />;
    }
    return <Outlet />;
};

const AuthGuard: React.FC = () => {
    const location = useLocation();
    const { permissions, initialized, menuTree } = usePermission();

    if (!isLoggedIn()) {
        return <Navigate to="/login" replace />;
    }
    if (isMustChangePassword()) {
        return <Navigate to="/change-password" replace />;
    }

    // 权限尚未完成加载，放行等待（PortalLayout 会显示 loading 状态）
    if (!initialized) {
        return <Outlet />;
    }

    // 权限加载完毕但无任何权限，跳转到无权限页面
    if (permissions.size === 0) {
        return <Navigate to="/no-permission" replace />;
    }

    // 根路径放行（PortalLayout 会处理跳转到第一个有权限的页面）
    if (location.pathname === '/') {
        return <Outlet />;
    }

    /*
     * 路由级放行以服务端菜单树为准，而不是只看权限码。
     * 权限码是账号维度，菜单树在其上叠加了客户维度过滤（如「允许查询异常称重信息」关闭时
     * 服务端剔除 portal:weighing-query）。只看码会把运营查询账号兜底跳到 /weighing-query，
     * 页面挂载即被服务端接口层拒绝、弹「无权限访问」，然后停在一个菜单里根本没有的页面上。
     * 菜单树为空时（极端：只有 BUTTON 码、无任何 MENU）退回权限码判断，保持旧行为。
     */
    const menuPaths = collectLeafPaths(menuTree ?? []);
    const useMenuTree = menuPaths.size > 0;
    const canVisit = (path: string) =>
        useMenuTree ? menuPaths.has(path) : permissions.has(`portal:${path.slice(1)}`);
    const fallbackPath = () => {
        if (useMenuTree) return findFirstLeafPath(menuTree) || '/no-permission';
        const firstAllowed = Object.keys(routeMap).find((path) => permissions.has(`portal:${path.slice(1)}`));
        return firstAllowed || '/no-permission';
    };

    // 路由级校验：只对 routeMap 中注册的页面做检查
    if (routeMap[location.pathname] && !canVisit(location.pathname)) {
        return <Navigate to={fallbackPath()} replace />;
    }

    if (location.pathname.startsWith('/bill-detail/') && !permissions.has('portal:bill-list:view')) {
        return <Navigate to={permissions.has('portal:bill-list') ? '/bill-list' : '/no-permission'} replace />;
    }

    // 订单详情独立权限码（不复用列表页权限码，直接改 URL 也需独立校验）
    if (location.pathname.startsWith('/order-detail/') && !permissions.has('portal:order-detail')) {
        return <Navigate to={permissions.has('portal:order-query') ? '/order-query' : '/no-permission'} replace />;
    }

    /*
     * 导出记录页复用导出按钮的权限码。这条判断不能省、也不能改成把该路径塞进 routeMap：
     * 上面那段是按 `portal:${pathname.slice(1)}` 反推权限码的，而 portal:order-query:export
     * 带冒号，任何路径都反推不出来——塞进 routeMap 的结果是持有权限的账号同样被 redirect。
     */
    if (location.pathname.startsWith(EXPORT_RECORDS_PATH) && !permissions.has(EXPORT_PERMISSION)) {
        return <Navigate to={permissions.has('portal:order-query') ? '/order-query' : '/no-permission'} replace />;
    }

    return <Outlet />;
};

export default AuthGuard;
